import { omit } from 'lodash';
import { useRef, useState } from 'react';

import {
  Box,
  TableBody,
  TableCell,
  TableColumn,
  TableRow,
} from '@project44/manifest-ds';
import { DateFormat, formatDateTime } from '@project44/ui-utils';
import { TrackedShipmentPosition } from '@project44-models/shipment-entity';

import { scopedT } from '../../../../utils/helpers/scopedT';

import {
  ActiveCircle,
  MarkerCell,
  StyledTable,
  StyledTableHeader,
  StyledTableRow,
  TableContainer,
} from './styles';

export interface Column {
  key: string;
  header: string;
}

interface LocationPingsTableProps {
  data: TrackedShipmentPosition[];
  onChangeActivePing: (pos: TrackedShipmentPosition) => void;
  watch?: (cb: (p: TrackedShipmentPosition) => void) => void;
}

type PositionRow = TrackedShipmentPosition & {
  displayDate: string;
  coords: string;
};

const t = scopedT();

export const LocationPingsTable = ({
  data,
  onChangeActivePing,
  watch,
}: LocationPingsTableProps) => {
  const tableRef = useRef(null);
  const [activePos, setActivepos] = useState<number | null>(null);

  const columns = [
    { key: 'displayDate', header: t('map.pings.timestamp') },
    { key: 'coords', header: t('map.pings.coordinates') },
  ];

  const positions: PositionRow[] = data.map((pos: TrackedShipmentPosition) => ({
    ...pos,
    coords:
      Number(pos?.latitude).toFixed(3) +
      ', ' +
      Number(pos?.longitude).toFixed(3),
    displayDate: formatDateTime({
      dateTime: String(pos.dateTime),
      dateFormat: DateFormat.monthDayYearHourMinuteAbbreviatedTimezone,
    }),
  }));

  watch?.((p: TrackedShipmentPosition) => {
    const activePosIdx = positions.findIndex(
      (i) =>
        i.dateTime == p?.dateTime &&
        i.latitude == p?.latitude &&
        i.longitude == p.longitude,
    );

    if (activePosIdx === -1) {
      setActivepos(null);
      return;
    }

    if (tableRef?.current) {
      setActivepos(activePosIdx);

      //scroll table to show selected in the middle of it
      const rowHeight = 53;
      (tableRef?.current as HTMLElement).scrollTo({
        top:
          rowHeight * (activePosIdx + 1) -
          (tableRef?.current as HTMLElement).offsetHeight / 2,
        behavior: 'smooth',
      });
    }
  });

  const renderTableRow = (item: PositionRow, idx: number) => {
    const isActive = activePos === idx;
    const rowProps = {
      active: isActive,
      'data-testid':
        activePos === idx ? `location-pings-active-row-${idx}` : '',
      onClick: () => {
        if (isActive) return;
        onChangeActivePing(omit(item, ['displayDate', 'coords']));
        setActivepos(idx);
      },
    };

    return (
      <StyledTableRow key={idx} {...rowProps}>
        <MarkerCell>{isActive && <ActiveCircle />}</MarkerCell>
        {columns.map((column) => {
          return (
            <TableCell key={column.key}>
              {item?.[column.key as 'displayDate' | 'coords'] || '-'}
            </TableCell>
          );
        })}
      </StyledTableRow>
    );
  };

  return (
    <Box data-testid="location-pings-items">
      <TableContainer ref={tableRef}>
        <StyledTable data-testid="location-pings-table">
          <StyledTableHeader>
            <TableRow>
              <TableColumn />
              {columns.map((column) => (
                <TableColumn key={column.key}>{column.header}</TableColumn>
              ))}
            </TableRow>
          </StyledTableHeader>
          <TableBody data-testid="location-pings-body">
            {positions.map(renderTableRow)}
          </TableBody>
        </StyledTable>
      </TableContainer>
    </Box>
  );
};

export default LocationPingsTable;
